import { createContext, ReactNode, useContext, useMemo, useState } from "react";

import useConfiguratorData from "../modules/Data/useConfiguratorData";
import { NONE } from "../modules/Data/static";
import type { iUteAccessorySelection } from "../modules/Data/types";

type SelectedUteAccessory = Pick<
  iUteAccessorySelection,
  "id" | "type" | "configuration" | "colour"
>;

type ConfiguratorContextProps = {
  configuratorAccessories: any[];
  initAccessory: any;
  selectedUteAccessory: SelectedUteAccessory;
  updateSelectedUteAccessory: (state: SelectedUteAccessory) => void;
};

type ConfiguratorProviderProps = {
  children: ReactNode;
};

export const ConfiguratorContext = createContext<ConfiguratorContextProps | undefined>(
  undefined,
);

export const useConfiguratorContext = () => {
  const context = useContext(ConfiguratorContext);

  if (!context) {
    throw new Error("useConfiguratorContext must be used within a ConfiguratorProvider");
  }

  return context;
};

export const ConfiguratorProvider = ({ children }: ConfiguratorProviderProps) => {
  const { getConfiguratorAccessories } = useConfiguratorData();

  const configuratorAccessories = getConfiguratorAccessories();

  const initAccessory = useMemo(
    () => configuratorAccessories.find((item) => item.selected),
    [configuratorAccessories],
  );

  const [selectedUteAccessory, setSelectedUteAccessory] = useState<SelectedUteAccessory>({
    id: initAccessory?.id ?? NONE,
    type: initAccessory?.type ?? NONE,
    configuration: initAccessory?.configuration ?? NONE,
    colour: initAccessory?.colour ?? NONE,
  });

  const updateSelectedUteAccessory = (state: SelectedUteAccessory) =>
    setSelectedUteAccessory((prevState) => ({ ...prevState, ...state }));

  const value = useMemo(
    () => ({
      configuratorAccessories,
      initAccessory,
      selectedUteAccessory,
      //
      updateSelectedUteAccessory,
    }),
    [configuratorAccessories, initAccessory, selectedUteAccessory],
  );

  return (
    <ConfiguratorContext.Provider value={value}>{children}</ConfiguratorContext.Provider>
  );
};
